/**
 * «¿Dónde y cuándo se hizo esta foto?», leído del EXIF del ORIGINAL.
 * Spec 2026-09-23 fotos geolocalizadas, § 16.
 *
 * ─── Por qué solo el principio ─────────────────────────────────────────────
 *
 * El EXIF va en el segmento APP1, pegado a la cabecera del JPEG: medido el
 * 2026-09-25, 128 KB bastan tanto para las fotos de nuestra cámara como para
 * las de galería de varios megas. El visor pide `bytes=0-131071` y, por si el
 * servidor ignora el `Range` y manda el fichero entero, deja de leer al llegar
 * al tope. Una foto abierta cuesta 128 KB, no los 3 MB del original.
 *
 * ─── Por qué del original ──────────────────────────────────────────────────
 *
 * Es lo único que conserva el EXIF: las miniaturas y el visor pasan por
 * Cloudinary, que lo borra (§ 2). Las fotos legacy de Cloudinary no tienen
 * original en Hetzner y el llamante ni lo intenta.
 */

/** Lo que el visor enseña de una foto. Todo `null` si no se sabe. */
export interface PhotoCapture {
  latitude: number | null;
  longitude: number | null;
  /** `GPSHPositioningError`, en metros. Sin él, `null`: no se inventa. */
  accuracyMeters: number | null;
  /**
   * `DateTimeOriginal` TAL CUAL, «AAAA:MM:DD HH:MM:SS»: la hora local que
   * marcó la cámara donde se hizo la foto. Sin convertir de zona (D25), igual
   * que las galerías.
   */
  takenAtLocal: string | null;
  /** La hizo la cámara de ROIS: su `Software` empieza por «ROIS» (D26). */
  roisCamera: boolean;
}

/** Cuánto se lee del original como mucho. */
export const CAPTURE_READ_BYTES = 128 * 1024;

const EMPTY: PhotoCapture = {
  latitude: null,
  longitude: null,
  accuracyMeters: null,
  takenAtLocal: null,
  roisCamera: false,
};

/**
 * Solo los bloques donde está lo que se pinta. `reviveValues: false` deja la
 * fecha como la cadena de la cámara (revivirla la interpretaría en la zona del
 * navegador); `latitude`/`longitude` salen en decimal igualmente.
 */
const EXIFR_OPTIONS = {
  tiff: true,
  ifd0: true,
  exif: true,
  gps: true,
  ifd1: false,
  interop: false,
  makerNote: false,
  userComment: false,
  xmp: false,
  icc: false,
  iptc: false,
  jfif: false,
  ihdr: false,
  reviveValues: false,
  translateValues: false,
  mergeOutput: true,
} as const;

const EXIF_DATE = /^\d{4}:\d{2}:\d{2} \d{2}:\d{2}:\d{2}$/;

const finite = (value: unknown): number | null =>
  typeof value === 'number' && Number.isFinite(value) ? value : null;

/** De lo que devuelve `exifr` a `PhotoCapture`. Puro. */
export function parseCaptureTags(
  tags: Record<string | number, unknown> | null | undefined,
): PhotoCapture {
  if (!tags) return { ...EMPTY };

  const lat = finite(tags.latitude);
  const lng = finite(tags.longitude);
  const hasGps =
    lat !== null &&
    lng !== null &&
    Math.abs(lat) <= 90 &&
    Math.abs(lng) <= 180 &&
    // 🔴 A ceros es como entrega Android una foto de galería a quien no tiene
    // permiso para ver su ubicación (§ 13.2): no es un sitio, es un «no sé».
    !(lat === 0 && lng === 0);

  // exifr pone nombre a 0x001F solo en algunas versiones: se acepta el número.
  const rawError = finite(tags.GPSHPositioningError ?? tags[0x001f]);
  const date = typeof tags.DateTimeOriginal === 'string' ? tags.DateTimeOriginal.trim() : '';
  const software = typeof tags.Software === 'string' ? tags.Software.trim() : '';

  return {
    latitude: hasGps ? lat : null,
    longitude: hasGps ? lng : null,
    accuracyMeters: hasGps && rawError !== null && rawError >= 0 ? rawError : null,
    takenAtLocal: EXIF_DATE.test(date) ? date : null,
    roisCamera: /^ROIS\b/.test(software),
  };
}

type ExifrParse = (
  input: Uint8Array,
  options: typeof EXIFR_OPTIONS,
) => Promise<Record<string | number, unknown> | undefined>;

/**
 * `exifr` en su build `lite` (45 KB; la `mini` no trae `Software` ni la
 * fecha), importado bajo demanda: va en un trozo aparte que solo se descarga
 * la primera vez que alguien abre el visor con el flag encendido.
 */
async function loadExifr(): Promise<ExifrParse> {
  const mod = (await import('exifr/dist/lite.esm.mjs')) as unknown as {
    default?: { parse: ExifrParse };
    parse?: ExifrParse;
  };
  const exifr = mod.default ?? (mod as { parse: ExifrParse });
  return exifr.parse.bind(exifr);
}

/** Lee como mucho `limit` bytes del cuerpo y corta la descarga. */
async function readHead(response: Response, limit: number): Promise<Uint8Array> {
  const reader = response.body?.getReader();
  if (!reader) {
    return new Uint8Array(await response.arrayBuffer()).subarray(0, limit);
  }
  const chunks: Uint8Array[] = [];
  let total = 0;
  try {
    while (total < limit) {
      const { done, value } = await reader.read();
      if (done || !value) break;
      chunks.push(value);
      total += value.length;
    }
  } finally {
    // Si el servidor ignoró el `Range`, esto corta el resto del fichero.
    void reader.cancel().catch(() => undefined);
  }
  const head = new Uint8Array(Math.min(total, limit));
  let offset = 0;
  for (const chunk of chunks) {
    const part = chunk.subarray(0, head.length - offset);
    head.set(part, offset);
    offset += part.length;
    if (offset >= head.length) break;
  }
  return head;
}

const isAbort = (err: unknown) =>
  err instanceof DOMException ? err.name === 'AbortError' : (err as Error)?.name === 'AbortError';

/**
 * Pide el principio del original y devuelve lo que dice su EXIF.
 *
 * - Un fichero sin EXIF (o que no es un JPEG) → `PhotoCapture` vacía: no es
 *   un error, es una foto sin datos.
 * - HTTP ≥ 400 → lanza: «no se ha podido leer» no es lo mismo que «no tiene».
 * - `cache: 'no-store'`: el bucket solo manda la cabecera CORS si la petición
 *   trae `Origin`, y una respuesta cacheada de un `<img>` no la tiene (ver
 *   `fetchDownloadBlob`).
 * - Si la petición con `Range` falla por red o CORS (un bucket que no admita
 *   la cabecera en el preflight), se reintenta UNA vez sin ella: el corte a
 *   128 KB sigue protegiendo.
 */
export async function readPhotoCapture(
  url: string,
  signal?: AbortSignal,
): Promise<PhotoCapture> {
  let response: Response;
  try {
    response = await fetch(url, {
      cache: 'no-store',
      headers: { Range: `bytes=0-${CAPTURE_READ_BYTES - 1}` },
      signal,
    });
  } catch (err) {
    if (isAbort(err) || signal?.aborted) throw err;
    response = await fetch(url, { cache: 'no-store', signal });
  }
  if (!response.ok) {
    throw new Error(`readPhotoCapture: HTTP ${response.status}`);
  }

  const head = await readHead(response, CAPTURE_READ_BYTES);
  const parse = await loadExifr();
  const tags = await parse(head, EXIFR_OPTIONS).catch(() => undefined);
  return parseCaptureTags(tags);
}
